import { getLogger } from "loglevel";
import type {
    AddVoiceParticipantOptions as CBMAddVoiceParticipantOptions,
    AddVoiceParticipantResponse
} from "~/modules/cbm";
import { ParticipantType, CbmImpl } from "~/modules/cbm";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { Action } from "~/modules/actions/Action";
import { LoggerName } from "~/modules/logger";
import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";

import { ErrorCode, ErrorSeverity } from "~/modules/error";
import {
    getReservationsByTask,
    getDefaultCallerID,
    checkWorker,
    checkReservationExists,
    checkRequiredParameter
} from "~/modules/actions/ActionUtils";
import { sendWorkerTrackingEvent, toCauseMessage } from "~/modules/telemetry/utils";
import { AccountConfigDataContainer } from "~/modules/config/AccountConfig/AccountConfigImpl/AccountConfigDataContainer/AccountConfigDataContainer";
import { EVENTS } from "~/modules/telemetry/Analytics";
import { createFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";




export interface AddExternalVoiceParticipantOptions extends CBMAddVoiceParticipantOptions {
    callerId?: string;
}




































































export class AddExternalVoiceParticipant implements Action<Promise<AddVoiceParticipantResponse>> {
    


    readonly #taskSid: string;

    


    readonly #phoneNumber: string;

    


    readonly #options?: AddExternalVoiceParticipantOptions;

    constructor(taskSid: string, phoneNumber: string, options?: AddExternalVoiceParticipantOptions) {
        this.#taskSid = taskSid;
        this.#phoneNumber = phoneNumber;
        this.#options = options;
    }

    async run(ctx: {}): Promise<AddVoiceParticipantResponse> {
        const context = ctx as ContextManager;
        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();
        const logger = getLogger(LoggerName.Actions);
        const worker = taskRouter.worker;
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        await cbm.init();
        const accountConfig = context.retrieveInstanceOf(
            ContextInstance.AccountConfigDataContainer,
            AccountConfigDataContainer
        );

        const { callerId, ...addVoiceParticipantOptions } = this.#options ?? {};

        const participantType = this.#options?.type ?? ParticipantType.External;

        logger.debug(
            `AddExternalVoiceParticipant invoked with #taskSid: ${this.#taskSid} and #phoneNumber: ${this.#phoneNumber}`
        );

        checkWorker(worker, {
            actionName: "AddExternalVoiceParticipant",
            moduleName: "actions/Voice",
            logger,
            analytics,
            context,
            event: EVENTS.AddExternalVoiceParticipantCompleted,
            extra: {
                taskSid: this.#taskSid,
                participantType
            },
            worker
        });

        const reservations = getReservationsByTask(worker, this.#taskSid);
        const acceptedReservations = reservations.filter((res) => res.status === "accepted");
        const reservation = acceptedReservations[0];

        checkReservationExists(reservation, this.#taskSid, {
            actionName: "AddExternalVoiceParticipant",
            moduleName: "actions/Voice",
            logger,
            analytics,
            context,
            event: EVENTS.AddExternalVoiceParticipantCompleted,
            extra: {
                taskSid: this.#taskSid,
                participantType
            },
            worker
        });

        checkRequiredParameter(this.#phoneNumber, "phoneNumber", {
            actionName: "AddExternalVoiceParticipant",
            moduleName: "actions/Voice",
            logger,
            analytics,
            context,
            event: EVENTS.AddExternalVoiceParticipantCompleted,
            extra: {
                taskSid: this.#taskSid,
                participantType
            },
            worker
        });

        const callerIdParam = callerId ?? getDefaultCallerID(accountConfig.get().outboundCallFlows);
        if (!callerIdParam) {
            const errorMsg = `AddExternalVoiceParticipant: Caller ID not found`;
            logger.error(errorMsg);
            sendWorkerTrackingEvent(EVENTS.AddExternalVoiceParticipantCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                participantType
            });
            return Promise.reject(
                createFlexSdkError(context)(ErrorCode.CallerIdUndefined, { severity: ErrorSeverity.Error }, errorMsg)
            );
        }

        try {
            const addVoiceParticipantResponse = await cbm.addVoiceParticipant(reservation.task, {
                from: callerIdParam,
                to: this.#phoneNumber,
                type: participantType,
                ...addVoiceParticipantOptions
            });
            sendWorkerTrackingEvent(EVENTS.AddExternalVoiceParticipantCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                participantType
            });
            return Promise.resolve(addVoiceParticipantResponse);
        } catch (error) {
            const errorMsg = `AddExternalVoiceParticipant: Failed to add external voice participant to task ${
                this.#taskSid
            }`;
            sendWorkerTrackingEvent(EVENTS.AddExternalVoiceParticipantCompleted, analytics, worker, {
                error: `${errorMsg} (${toCauseMessage(error)})`,
                taskSid: this.#taskSid,
                participantType
            });
            return Promise.reject(
                createFlexSdkError(context)(
                    ErrorCode.FailedToAddExternalVoiceParticipant,
                    { severity: ErrorSeverity.Error },
                    errorMsg,
                    error
                )
            );
        }
    }
}
